import { MS_PER_DAY } from "@/lib/constants";
import {
  CommissionLedgerEntryType,
  EarningsEmploymentStatus,
  EarningsPaymentStatus,
  EarningsPeriod,
  EarningsPlacement,
  EarningsTrackerFilter,
  EarningsTrackerSortKey,
  EarningsTrackerSortState,
  SortOrder,
} from "@/types";

export const earningsTracker = {
  // ACCEPTED rows haven't started yet, so their date lives in dateSetToStart
  getStartDateIso: (placement: EarningsPlacement): string | null =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    placement.dateStarted || placement.dateSetToStart,

  // Day the placement clears its backfill guarantee
  getRetentionDateIso: (placement: EarningsPlacement): string | null => {
    const startDateIso = earningsTracker.getStartDateIso(placement);
    if (!startDateIso) return null;

    return new Date(
      new Date(startDateIso).getTime() +
        placement.backfillGuaranteeDays * MS_PER_DAY,
    ).toISOString();
  },

  getDaysUntilRetention: (placement: EarningsPlacement): number | null => {
    const retentionDateIso = earningsTracker.getRetentionDateIso(placement);
    if (!retentionDateIso) return null;

    return Math.ceil(
      (new Date(retentionDateIso).getTime() - Date.now()) / MS_PER_DAY,
    );
  },

  isPastRetentionDate: (placement: EarningsPlacement): boolean => {
    const daysUntil = earningsTracker.getDaysUntilRetention(placement);
    return daysUntil !== null && daysUntil <= 0;
  },

  // Made it = someone confirmed it, or the retention date has passed
  hasMadeIt: (placement: EarningsPlacement): boolean =>
    !!placement.confirmedPastBackfillGuarantee ||
    earningsTracker.isPastRetentionDate(placement),

  getEmploymentStatus: (
    placement: EarningsPlacement,
  ): EarningsEmploymentStatus => {
    if (placement.dateEmploymentEnded) return EarningsEmploymentStatus.CHURNED;
    if (placement.dateStarted) return EarningsEmploymentStatus.STARTED;
    return EarningsEmploymentStatus.NOT_STARTED;
  },

  getPaymentStatus: (placement: EarningsPlacement): EarningsPaymentStatus => {
    const hasEntryOfType = (type: CommissionLedgerEntryType) =>
      placement.ledgerEntries.some((entry) => entry.type === type);

    if (hasEntryOfType(CommissionLedgerEntryType.CLAWBACK)) {
      return EarningsPaymentStatus.CLAWBACK;
    }
    if (hasEntryOfType(CommissionLedgerEntryType.PAID)) {
      return EarningsPaymentStatus.PAID;
    }
    return EarningsPaymentStatus.UNPAID;
  },

  // What was actually paid out (PAID entries only) — the strikethrough base
  // on adjusted rows and the amount a clawback reverses.
  getPaidDollars: (placement: EarningsPlacement): number =>
    placement.ledgerEntries
      .filter((entry) => entry.type === CommissionLedgerEntryType.PAID)
      .reduce((total, entry) => total + entry.amount, 0),

  hasAdjustments: (placement: EarningsPlacement): boolean =>
    placement.ledgerEntries.some(
      (entry) => entry.type === CommissionLedgerEntryType.ADJUSTMENT,
    ),

  // What the row was worth before the clawback reversed it (PAID + ADJUSTMENT)
  // — shown on clawed rows so the staffer sees what they lost
  getPreClawbackDollars: (placement: EarningsPlacement): number =>
    placement.ledgerEntries
      .filter((entry) => entry.type !== CommissionLedgerEntryType.CLAWBACK)
      .reduce((total, entry) => total + entry.amount, 0),

  // Pre-adjustment rate, derived from what was paid against the revenue —
  // no stored history needed
  getOriginalRatePercent: (placement: EarningsPlacement): number | null => {
    const paidDollars = earningsTracker.getPaidDollars(placement);
     
    if (!paidDollars || !placement.revenueEarned) return null;

    return (
      Math.round((paidDollars / placement.revenueEarned) * 100 * 100) / 100
    );
  },

  // Paid rows show what the ledger nets out to; everything else shows the
  // projected revenue × rate.
  getCommissionDollars: (placement: EarningsPlacement): number | null => {
    if (placement.ledgerEntries.length > 0) {
      return placement.ledgerEntries.reduce(
        (total, entry) => total + entry.amount,
        0,
      );
    }

    if (placement.revenueEarned !== null && placement.commissionRate !== null) {
      return (placement.revenueEarned * placement.commissionRate) / 100;
    }

    return null;
  },

  matchesFilter: (
    placement: EarningsPlacement,
    filter: EarningsTrackerFilter,
    viewedUserId?: number,
  ): boolean => {
    const paymentStatus = earningsTracker.getPaymentStatus(placement);
    const employmentStatus = earningsTracker.getEmploymentStatus(placement);

    switch (filter) {
      case EarningsTrackerFilter.ALL:
        return true;
      case EarningsTrackerFilter.PAID:
        return paymentStatus === EarningsPaymentStatus.PAID;
      case EarningsTrackerFilter.UNPAID:
        // Claimed churns have nothing coming; unfilled churns can still be saved
        return (
          paymentStatus === EarningsPaymentStatus.UNPAID &&
          !placement.isSuperseded
        );
      case EarningsTrackerFilter.PROBATIONARY:
        return (
          employmentStatus === EarningsEmploymentStatus.STARTED &&
          !earningsTracker.hasMadeIt(placement)
        );
      case EarningsTrackerFilter.BACKFILL:
        return (
          employmentStatus === EarningsEmploymentStatus.CHURNED &&
          !placement.isSuperseded
        );
      case EarningsTrackerFilter.CLAIMED:
        // Backfilling your own churn isn't a claim by someone else
        return (
          placement.isSuperseded &&
          placement.backfilledByUserId !== viewedUserId
        );
    }
  },

  // Calendar window a period covers; ALL_YEAR spans the whole current year
  getPeriodRange: (period: EarningsPeriod): { start: Date; end: Date } => {
    const now = new Date();
    const year = now.getFullYear();
    const quarterStartMonth = now.getMonth() - (now.getMonth() % 3);

    switch (period) {
      case EarningsPeriod.ALL_YEAR:
        return { start: new Date(year, 0, 1), end: new Date(year + 1, 0, 1) };
      case EarningsPeriod.THIS_MONTH:
        return {
          start: new Date(year, now.getMonth(), 1),
          end: new Date(year, now.getMonth() + 1, 1),
        };
      case EarningsPeriod.THIS_QUARTER:
        return {
          start: new Date(year, quarterStartMonth, 1),
          end: new Date(year, quarterStartMonth + 3, 1),
        };
      case EarningsPeriod.LAST_QUARTER:
        return {
          start: new Date(year, quarterStartMonth - 3, 1),
          end: new Date(year, quarterStartMonth, 1),
        };
    }
  },

  // Rows live in the period their credited date falls in; rows clawed back
  // inside the window resurface too. Past years only support the full-year
  // window.
  matchesPeriod: (
    placement: EarningsPlacement,
    period: EarningsPeriod,
    year: number,
  ): boolean => {
    const isCurrentYear = year === new Date().getFullYear();
    const range = isCurrentYear
      ? earningsTracker.getPeriodRange(period)
      : { start: new Date(year, 0, 1), end: new Date(year + 1, 0, 1) };

    if (!placement.creditedDate) {
      return isCurrentYear && period === EarningsPeriod.ALL_YEAR;
    }

    const creditedDate = new Date(placement.creditedDate);
    if (creditedDate >= range.start && creditedDate < range.end) return true;

    return placement.ledgerEntries.some(
      (entry) =>
        entry.type === CommissionLedgerEntryType.CLAWBACK &&
        new Date(entry.effectiveDate) >= range.start &&
        new Date(entry.effectiveDate) < range.end,
    );
  },

  // Out-of-year rows resurfaced by a clawback get tagged with their own year
  getOriginYear: (
    placement: EarningsPlacement,
    viewedYear: number,
  ): number | null => {
    if (!placement.creditedDate) return null;

    const creditedYear = new Date(placement.creditedDate).getFullYear();
    return creditedYear === viewedYear ? null : creditedYear;
  },

  // `null` means "missing" — those rows always sink to the end of the list
  getSortValue: (
    placement: EarningsPlacement,
    key: EarningsTrackerSortKey,
  ): string | number | null => {
    switch (key) {
      case EarningsTrackerSortKey.CANDIDATE:
        return placement.candidateName.toLowerCase();
      case EarningsTrackerSortKey.CREDITED_DATE:
        return placement.creditedDate;
      case EarningsTrackerSortKey.START_DATE:
        return earningsTracker.getStartDateIso(placement);
      case EarningsTrackerSortKey.RETENTION:
        return earningsTracker.getRetentionDateIso(placement);
      case EarningsTrackerSortKey.REVENUE:
        return placement.revenueEarned;
      case EarningsTrackerSortKey.COMMISSION:
        return earningsTracker.getCommissionDollars(placement);
    }
  },

  sortPlacements: (
    placements: EarningsPlacement[],
    sort: EarningsTrackerSortState | null,
  ): EarningsPlacement[] => {
    if (!sort) return placements;

    return [...placements].sort((a, b) => {
      const aValue = earningsTracker.getSortValue(a, sort.key);
      const bValue = earningsTracker.getSortValue(b, sort.key);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      // createdAt tie-breaker keeps equal rows from flipping when a row updates
      const comparison =
        typeof aValue === "string" && typeof bValue === "string"
          ? aValue.localeCompare(bValue)
          : Number(aValue) - Number(bValue);
      const tieBrokenComparison =
         
        comparison || a.createdAt.localeCompare(b.createdAt);

      return sort.direction === SortOrder.Asc
        ? tieBrokenComparison
        : -tieBrokenComparison;
    });
  },
};
